import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Tooltip,
  Typography,
} from "@mui/material";
import type { FC, ReactNode } from "react";
import { useFormContext } from "react-hook-form";
import { useAsyncStateSupabaseCallback } from "../../../../utils/hooks/use-async-state-callback";
import { useDialog } from "../../../../utils/hooks/use-dialog";
import { propertyNotEmpty } from "../../../../utils/not-empty";
import type { UUID } from "../../../../utils/uuid";
import { uuidv4 } from "../../../../utils/uuid";
import { SupabaseEmailDesignMomentInfoService } from "../../../moment/server/supabase-moment-service";
import type { EmailDesign } from "../../server/supabase-email-design-service";
import type { EmailDesignSchema } from "../../types/email-design-type";

export const SaveButton: FC<{
  save: (id: UUID, data: EmailDesignSchema) => void;
  saving: boolean;
  emailDesign?: EmailDesign;
}> = ({ save, saving, emailDesign }) => {
  const dialog = useDialog<{
    data: EmailDesignSchema;
    moments: { id: UUID; title: string }[];
  }>();

  const { loading: momentsLoading, load: loadMoments } = useAsyncStateSupabaseCallback(
    async ({ supabase, account_id }, design?: EmailDesign) => {
      if (!design) return [];

      const { data, error } = await new SupabaseEmailDesignMomentInfoService(supabase).getAll(
        account_id,
        {
          filter: [
            {
              key: "email_design_id",
              operator: "eq",
              value: design.id,
            },
            {
              key: "status",
              operator: "eq",
              value: "active",
            },
          ],
          order: [{ column: "id" }],
        },
      );

      if (error) throw error;

      return data.filter(propertyNotEmpty("title")).filter(propertyNotEmpty("id"));
    },
    [],
  );

  const { handleSubmit } = useFormContext<EmailDesignSchema>();

  return (
    <>
      <Button
        data-analytics-id="email-design-save-changes"
        loading={momentsLoading || saving}
        type="button"
        variant="contained"
        id="saveEmailDesignButton"
        disabled={momentsLoading || saving}
        onClick={() =>
          void handleSubmit((data) => {
            void loadMoments(emailDesign)
              .then((momentData) => {
                momentData && momentData.length > 0
                  ? dialog.handleOpen({ data, moments: momentData })
                  : save(emailDesign?.id ?? uuidv4(), data);
              })

              .catch(console.error);
          }, console.error)().catch(console.error)
        }
      >
        Save Changes
      </Button>
      <Dialog open={dialog.open} onClose={dialog.handleClose}>
        <DialogTitle>Update Email Design</DialogTitle>
        {dialog.data && (
          <>
            <DialogContent>
              <HasMomentsWarning
                templateName={dialog.data.data.name}
                moments={dialog.data.moments}
              />
            </DialogContent>
            <DialogActions>
              <Button
                data-analytics-id="email-design-save-dialog-cancel"
                loading={saving || momentsLoading}
                onClick={dialog.handleClose}
                variant="text"
                color="primary"
              >
                Cancel
              </Button>
              <Button
                data-analytics-id="email-design-save-dialog-save"
                loading={saving || momentsLoading}
                onClick={() => save(emailDesign?.id ?? uuidv4(), dialog.data!.data)}
                variant="contained"
                color="primary"
              >
                Save
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

const HasMomentsWarning: FC<{ templateName: string; moments: { id: UUID; title: string }[] }> = ({
  templateName,
  moments,
}) => {
  const dialog = useDialog();

  return (
    <>
      <DialogContentText>
        {`Are you sure you want to make changes to the "${templateName}" email template?`}
      </DialogContentText>
      <DialogContentText>
        This will update the email templates used in{" "}
        <OpenDialogLink onClick={dialog.handleOpen}>
          {`${moments.length} active Communication${moments.length === 1 ? "" : "s"}.`}
        </OpenDialogLink>
      </DialogContentText>
      <Dialog open={dialog.open} onClose={dialog.handleClose}>
        <DialogTitle>Impacted Communications</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            The following Communications will be impacted by this change:
          </Typography>
          <ul>
            {moments.map((moment) => (
              <li key={moment.id}>
                <Link href={`/moments/${moment.id}`} target="_blank">
                  {moment.title}
                </Link>
              </li>
            ))}
          </ul>
        </DialogContent>
        <DialogActions>
          <Button
            data-analytics-id="email-design-moments-warning-close"
            onClick={dialog.handleClose}
            variant="text"
            color="primary"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const OpenDialogLink: FC<{ onClick: () => void; children: ReactNode }> = ({
  onClick,
  children,
}) => {
  return (
    <Tooltip title="Click to see the impacted Communications">
      <Button
        data-analytics-id="email-design-save-dialog-see-moments"
        variant="text"
        color="primary"
        onClick={onClick}
      >
        {children}
      </Button>
    </Tooltip>
  );
};
